import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { signOutRequest } from "../../redux/auth/auth.actions";
import { toastify } from "../../configs/utils";
import { cardHasLongTitle } from "./CardsView.utils";

const DEFAULT_BASE_URL = "http://localhost:8000";
const API_BASE_URL =
  import.meta.env.VITE_BASE_URL_DEVELOPMENT || DEFAULT_BASE_URL;

// Styled Components
const InspectorWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 1200px;
`;

const InspectorContainer = styled.div`
  display: flex;
  height: 70vh;
  border: 1px solid rgba(17, 24, 39, 0.08);
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04), 0 6px 16px rgba(0, 0, 0, 0.07),
    inset 0 1px 0 rgba(255, 255, 255, 0.6);
  overflow: hidden;
  overflow-x: auto;

  /* Custom thin scrollbar */
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f1f5f9;

  &::-webkit-scrollbar {
    height: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
    transition: background 0.2s ease;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
`;

const CardListColumn = styled.div`
  flex: 2;
  min-width: 300px;
  background: #f3f3f3;
  border-right: 1px solid #e5e7eb;
  overflow-y: auto;
  display: flex;
  flex-direction: column;

  /* Scrollbar hidden until hover */
  scrollbar-width: none;

  &::-webkit-scrollbar {
    width: 0;
  }

  &:hover {
    scrollbar-width: thin;
    scrollbar-color: #cbd5e1 #f1f5f9;
  }

  &:hover::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
    transition: background 0.2s ease;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
`;

const ColumnHeader = styled.div`
  box-sizing: border-box;
  min-height: 60px;
  padding: 1rem;
  display: flex;
  align-items: center;
  border-bottom: 1px solid rgba(17, 24, 39, 0.16);
  background: #ededed;
  font-weight: 600;
  color: #4b5563;
  font-size: 0.875rem;
  position: sticky;
  top: 0;
  z-index: 10;
`;

const CardListHeader = styled(ColumnHeader)`
  /* Inherits all styles from ColumnHeader */
  justify-content: space-between;
`;

const ImageHeader = styled(ColumnHeader)`
  align-self: stretch;
  width: 100%;
`;

const InfoHeader = styled(ColumnHeader)`
  /* Inherits all styles from ColumnHeader */
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const BUTTON_TONES = {
  blue: { border: "#3b82f6", text: "#3b82f6", hoverBg: "#eff6ff" },
  green: { border: "#10b981", text: "#10b981", hoverBg: "#f0fdf4" },
  amber: { border: "#f59e0b", text: "#b45309", hoverBg: "#fffbeb" },
  grey: { border: "#d1d5db", text: "#374151", hoverBg: "#f9fafb" },
  red: { border: "#ef4444", text: "#ef4444", hoverBg: "#fef2f2" },
};

const ReadyButton = styled.button`
  padding: 0.4rem 0.85rem;
  background: white;
  border: 1px solid #e5e7eb;
  color: ${(props) => BUTTON_TONES[props.$tone || "amber"].text};
  border-radius: 8px;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);

  &:hover:not(:disabled) {
    background: ${(props) => BUTTON_TONES[props.$tone || "amber"].hoverBg};
    border-color: ${(props) => BUTTON_TONES[props.$tone || "amber"].border};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

const ToggleSwitchContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  user-select: none;
`;

const ToggleSwitchLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 500;
  color: #374151;
`;

const ToggleSwitch = styled.div`
  position: relative;
  width: 44px;
  height: 24px;
  background: ${(props) => (props.active ? "#3b82f6" : "#d1d5db")};
  border-radius: 12px;
  transition: background 0.2s ease;
  cursor: pointer;

  &:hover {
    background: ${(props) => (props.active ? "#2563eb" : "#9ca3af")};
  }
`;

const ToggleSwitchSlider = styled.div`
  position: absolute;
  top: 2px;
  left: ${(props) => (props.active ? "22px" : "2px")};
  width: 20px;
  height: 20px;
  background: white;
  border-radius: 50%;
  transition: left 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
`;

const CardListItem = styled.div`
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: all 0.2s ease, transform 0.3s ease, margin-top 0.3s ease,
    opacity 0.3s ease;
  margin-top: ${(props) => (props.$isAnimating ? "10px" : "0")};
  opacity: ${(props) =>
    props.$isAnimating && props.status === "ready" ? "0.7" : "1"};
  position: relative;
  width: 100%;
  box-sizing: border-box;
  min-width: 0;

  /* Border styling - full border when selected, left border for status */
  border-top: ${(props) => {
    if (props.selected) return "2px solid #9ca3af"; // Grey border for selected
    return "none";
  }};
  border-right: none;
  border-bottom: ${(props) => {
    if (props.selected) return "2px solid #9ca3af"; // Grey border for selected
    return "1px solid #e5e7eb";
  }};
  border-left: ${(props) => {
    if (props.selected) return "6px solid transparent"; // Transparent left border for selected
    if (props.status === "listed") return "5px solid transparent"; // Transparent border for listed
    if (props.status === "ready") return "5px solid transparent"; // Transparent border for ready
    if (props.status === "draft" || !props.status)
      return "5px solid transparent"; // Transparent border for draft
    return "5px solid transparent";
  }};

  /* Background - matches the cards table row styling */
  background: ${(props) => {
    if (props.$isAnimating && props.status === "ready") return "#fafbfc";
    if (props.selected) return "#eff6ff"; // Light blue background for selected
    if (props.$hasLongTitle) return "#fef2f2";
    return "#f3f3f3";
  }};

  /* Transform for animation only */
  transform: ${(props) => {
    if (props.$isAnimating) return "translateY(10px)";
    return "translateY(0)";
  }};

  &:hover {
    background: ${(props) => {
      if (props.$isAnimating && props.status === "ready") return "#f3f4f6";
      if (props.selected) return "#dbeafe"; // Slightly darker blue on hover when selected
      return "#ececec";
    }};
    transform: ${(props) => {
      if (props.$isAnimating) return "translateY(10px)";
      return "translateX(2px)";
    }};
  }

  &:last-child {
    border-bottom: none;
  }
`;

const StatusBadge = styled.span`
  position: absolute;
  top: 0.75rem;
  right: 1rem;
  display: inline-flex;
  align-items: center;
  padding: 0.125rem 0.5rem;
  border-radius: 999px;
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: capitalize;
  color: ${(props) => {
    if (props.status === "listed") return "#047857"; // Green for listed
    if (props.status === "ready") return "#b45309"; // Amber for ready
    if (props.status === "draft" || !props.status) return "#1d4ed8"; // Blue for draft
    return "#4b5563";
  }};
  background: ${(props) => {
    if (props.status === "listed") return "#d1fae5";
    if (props.status === "ready") return "#fef3c7";
    if (props.status === "draft" || !props.status) return "#dbeafe";
    return "#f3f4f6";
  }};
  line-height: 1.2;
  flex-shrink: 0;
  z-index: 1;
`;

const RadioButton = styled.input`
  margin-right: 0.75rem;
  cursor: pointer;
  flex-shrink: 0;
`;

const CardImages = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-right: 0.75rem;
  flex-shrink: 0;
`;

const CardThumbnail = styled.img`
  width: 32px;
  height: 45px;
  object-fit: cover;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
`;

const CardInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding-right: 4rem; /* Space for status badge */
  overflow: hidden;
`;

const CardTitle = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: #111827;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  max-width: 100%;
`;

const CardSubtitle = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
  line-height: 1.3;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.25rem;
`;

const MiddleColumn = styled.div`
  flex: 3;
  min-width: 350px;
  background: #f3f3f3;
  border-right: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  overflow-y: auto;

  /* Scrollbar hidden until hover */
  scrollbar-width: none;

  &::-webkit-scrollbar {
    width: 0;
  }

  &:hover {
    scrollbar-width: thin;
    scrollbar-color: #cbd5e1 #f1f5f9;
  }

  &:hover::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
    transition: background 0.2s ease;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
`;

const ImagePreviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  width: 100%;
  min-height: 100%;
  justify-content: flex-start;
  padding: 2rem;
`;

const ImagePreview = styled.img`
  max-width: 100%;
  max-height: 350px;
  object-fit: contain;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  border: 2px solid #e5e7eb;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: scale(1.02);
    box-shadow: 0 8px 12px rgba(0, 0, 0, 0.15);
  }
`;

const ImageLabel = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
`;

const NoSelectionMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  color: #6b7280;
  font-size: 0.875rem;
  text-align: center;
  line-height: 1.5;
  width: 100%;
`;

const RightColumn = styled.div`
  flex: 5;
  min-width: 450px;
  background: #f3f3f3;
  overflow-y: auto;
  display: flex;
  flex-direction: column;

  /* Scrollbar hidden until hover */
  scrollbar-width: none;

  &::-webkit-scrollbar {
    width: 0;
  }

  &:hover {
    scrollbar-width: thin;
    scrollbar-color: #cbd5e1 #f1f5f9;
  }

  &:hover::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
    transition: background 0.2s ease;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 1.5rem;
  /* reserve space at bottom so the sticky SaveBar doesn't cover inputs */
  padding-bottom: 6rem;
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #374151;
  border-bottom: 2px solid #e5e7eb;
  padding-bottom: 0.5rem;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FieldLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const EditedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  background: #dbeafe;
  color: #1d4ed8;
  font-size: 0.6875rem;
  font-weight: 600;
  padding: 0.125rem 0.5rem;
  border-radius: 999px;
`;

const FieldInput = styled.input`
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  color: #374151;
  background: white;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &:hover {
    border-color: #94a3b8;
  }

  &:disabled {
    background: #f9fafb;
    color: #9ca3af;
    cursor: not-allowed;
  }
`;

const FieldHelperText = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
  font-style: italic;
  margin-top: -0.25rem;
`;

const CurrencyInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const CurrencyPrefix = styled.span`
  position: absolute;
  left: 0.75rem;
  font-size: 0.875rem;
  color: #374151;
  pointer-events: none;
  z-index: 1;
`;

const CurrencyInput = styled(FieldInput)`
  padding-left: 2rem;
`;

const FieldTextarea = styled.textarea`
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  color: #374151;
  background: white;
  min-height: 80px;
  resize: vertical;
  font-family: inherit;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &:hover {
    border-color: #94a3b8;
  }

  &:disabled {
    background: #f9fafb;
    color: #9ca3af;
    cursor: not-allowed;
  }
`;

const FieldSelect = styled.select`
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  color: #374151;
  background: white;
  cursor: pointer;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &:hover {
    border-color: #94a3b8;
  }
`;

const NextButton = styled.button`
  padding: 0.5rem 1rem;
  background: white;
  border: 1px solid #e5e7eb;
  color: #3b82f6;
  border-radius: 8px;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  margin-right: 0.5rem;

  &:hover:not(:disabled) {
    background: #eff6ff;
    border-color: #3b82f6;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

const PrevButton = styled.button`
  padding: 0.5rem 1rem;
  background: white;
  border: 1px solid #e5e7eb;
  color: #374151;
  border-radius: 8px;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  margin-right: 0.5rem;

  &:hover:not(:disabled) {
    background: #f9fafb;
    border-color: #9ca3af;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

const SaveBar = styled.div`
  position: sticky;
  bottom: 0;
  background: #ededed;
  border-top: 1px solid rgba(17, 24, 39, 0.16);
  padding: 0.75rem 1.5rem;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  z-index: 20;
`;

const InspectorPagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  margin-top: 1rem;
`;

const InspectorPaginationButton = styled.button`
  padding: 0.4rem 0.9rem;
  background: white;
  border: 1px solid #e5e7eb;
  color: #3b82f6;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  min-width: auto;

  &:hover:not(:disabled) {
    background: #eff6ff;
    border-color: #3b82f6;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

const InspectorPageNumber = styled.span`
  font-size: 0.8rem;
  color: #374151;
  font-weight: 500;
`;

const NoCardMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  color: #6b7280;
  font-size: 0.875rem;
  text-align: center;
  line-height: 1.5;
`;

const DeleteButton = styled.button`
  padding: 0.5rem 1rem;
  background: white;
  border: 1px solid #e5e7eb;
  color: #ef4444;
  border-radius: 8px;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);

  &:hover:not(:disabled) {
    background: #fef2f2;
    border-color: #ef4444;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

const TabContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const TabButton = styled.button`
  padding: 0.5rem 1rem;
  background: ${(props) => (props.active ? "#3b82f6" : "#f3f4f6")};
  color: ${(props) => (props.active ? "white" : "#6b7280")};
  border: none;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  text-transform: uppercase;
  letter-spacing: 0.05em;

  &:hover {
    background: ${(props) => (props.active ? "#2563eb" : "#e5e7eb")};
    color: ${(props) => (props.active ? "white" : "#374151")};
  }

  &:active {
    transform: scale(0.98);
  }
`;

const CardInspector = ({
  cards,
  selectedCard,
  onCardSelect,
  selectedImage,
  setSelectedImage,
  setSelectedCard,
  onSaveCard,
  onImageClick,
  batchTitleBuilderConfig,
  onRefresh,
  columnOrder = [],
  columnVisibility = {},
  currentPage = 1,
  totalCount = 0,
  cardsPerPage = 100,
  nextPage = null,
  prevPage = null,
  onFetchPage,
}) => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  // Persist edit mode across refresh/unmount; only explicit "Cancel Editing" should turn it off.
  const [isEditing, setIsEditing] = useState(() => {
    try {
      return localStorage.getItem("cardInspector_isEditing") === "true";
    } catch {
      return false;
    }
  });
  const [animatingCardId, setAnimatingCardId] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const lastSavedFormDataRef = useRef(null);
  const isAutoSavingRef = useRef(false);
  const cardItemRefs = useRef({});
  
  // Initialize draftsOnly from localStorage to preserve state across refreshes
  const [draftsOnly, setDraftsOnly] = useState(() => {
    const saved = localStorage.getItem("cardInspector_draftsOnly");
    return saved === "true";
  });

  // Save draftsOnly to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("cardInspector_draftsOnly", draftsOnly.toString());
  }, [draftsOnly]);

  // Save edit mode to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem("cardInspector_isEditing", isEditing ? "true" : "false");
    } catch {
      // ignore storage errors (private mode / disabled storage)
    }
  }, [isEditing]);

  // Filter cards based on draftsOnly toggle
  // Filter cards based on draftsOnly toggle
  const filteredCards = draftsOnly
    ? cards.filter((card) => card.status === "draft" || !card.status)
    : cards;

  // Update selected card if it's filtered out (skip while ready animation runs)
  useEffect(() => {
    if (animatingCardId) return;
    if (draftsOnly && selectedCard) {
      const isSelectedCardInFiltered = filteredCards.some(
        (card) => card.id === selectedCard.id
      );
      if (!isSelectedCardInFiltered && filteredCards.length > 0) {
        onCardSelect(filteredCards[0]);
      }
    }
  }, [draftsOnly, selectedCard, filteredCards, onCardSelect, animatingCardId]);

  // Update form data when selected card changes
  useEffect(() => {
    if (selectedCard) {
      // Determine title value based on isTitleEdited flag
      const titleValue = selectedCard.isTitleEdited === true
        ? (selectedCard.metadata?.ebay_listing?.Title || formatTitleBuilderConfig(selectedCard))
        : formatTitleBuilderConfig(selectedCard);

      const initialFormData = {
        title: titleValue,
        description: selectedCard.metadata?.description || "",
        category: selectedCard.metadata?.category || "",
        suggested_price: selectedCard.metadata?.suggested_price || "",
        available_quantity:
          selectedCard.metadata?.ebay_listing?.AvailableQuantity || "",
        condition: selectedCard.metadata?.condition || "",
        status: selectedCard.status || "draft",
        detailed_metadata: {
          "Card Game / Type":
            selectedCard.metadata?.detailed_metadata?.["Card Game / Type"] ||
            "",
          "Card Name":
            selectedCard.metadata?.detailed_metadata?.["Card Name"] || "",
          "Card Number":
            selectedCard.metadata?.detailed_metadata?.["Card Number"] || "",
          Set: selectedCard.metadata?.detailed_metadata?.["Set"] || "",
          SubSet: selectedCard.metadata?.detailed_metadata?.["SubSet"] || "",
          Variant: selectedCard.metadata?.detailed_metadata?.["Variant"] || "",
          Year: selectedCard.metadata?.detailed_metadata?.["Year"] || "",
          Rarity: selectedCard.metadata?.detailed_metadata?.["Rarity"] || "",
          Edition: selectedCard.metadata?.detailed_metadata?.["Edition"] || "",
          Autograph:
            selectedCard.metadata?.detailed_metadata?.["Autograph"] || "",
          Language:
            selectedCard.metadata?.detailed_metadata?.["Language"] || "",
          Graded: selectedCard.metadata?.detailed_metadata?.["Graded"] || "",
          Grade: selectedCard.metadata?.detailed_metadata?.["Grade"] || "",
          Condition:
            selectedCard.metadata?.detailed_metadata?.["Condition"] || "",
          Producer:
            selectedCard.metadata?.detailed_metadata?.["Producer"] || "",
          "Special Features":
            selectedCard.metadata?.detailed_metadata?.["Special Features"] ||
            "",
          Damage: selectedCard.metadata?.detailed_metadata?.["Damage"] || "",
          "Selling Points":
            selectedCard.metadata?.detailed_metadata?.["Selling Points"] || "",
          "Market Value":
            selectedCard.metadata?.detailed_metadata?.["Market Value"] || "",
        },
        ebay_listing: (() => {
          const ebayListing = selectedCard.metadata?.ebay_listing || {};
          const result = {
            // Set Title based on isTitleEdited flag
            Title: titleValue,
          };

          // Copy all top-level keys except ItemSpecifics and Title (already set above)
          Object.keys(ebayListing).forEach((key) => {
            if (
              key !== "ItemSpecifics" &&
              key !== "Title" &&
              typeof ebayListing[key] !== "object"
            ) {
              result[key] = ebayListing[key] || "";
            }
          });

          // Copy ItemSpecifics if it exists
          if (ebayListing.ItemSpecifics) {
            result.ItemSpecifics = {};
            Object.keys(ebayListing.ItemSpecifics).forEach((key) => {
              result.ItemSpecifics[key] = ebayListing.ItemSpecifics[key] || "";
            });
          } else {
            result.ItemSpecifics = {};
          }

          return result;
        })(),
      };
      setFormData(initialFormData);
      lastSavedFormDataRef.current = initialFormData;
    }
  }, [selectedCard, batchTitleBuilderConfig]);

  // Keep the selected card visible in the left column when navigating with Prev/Next
  useEffect(() => {
    if (!selectedCard?.id) return;
    const scrollToSelected = () => {
      const el = cardItemRefs.current[selectedCard.id];
      if (el) {
        el.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    };
    requestAnimationFrame(scrollToSelected);
  }, [selectedCard?.id]);

  // Helper function to check if a field is a Yes/No field
  const isYesNoField = (fieldName) => {
    const yesNoFields = ["Graded", "Vintage", "Autographed", "Autograph"];
    return yesNoFields.includes(fieldName);
  };

  // Helper function to get condition options based on category
  const getConditionOptions = (category) => {
    if (category === "CCG Individual Card") {
      return [
        "Near Mint or Better",
        "Lightly Played (Excellent)",
        "Moderately Played (Very Good)",
        "Heavily Played (Poor)",
      ];
    } else if (
      category === "Sport Trading Card" ||
      category === "Non-Sport Trading Card" ||
      category === "Collectible Sports Sticker Singles" ||
      category === "Collectible Non-Sport Sticker Singles"
    ) {
      return ["Near Mint or Better", "Excellent", "Very Good", "Poor"];
    } else {
      // Default: show all options if category is not set or unknown
      return [
        "Near Mint or Better",
        "Lightly Played (Excellent)",
        "Moderately Played (Very Good)",
        "Heavily Played (Poor)",
        "Excellent",
        "Very Good",
        "Poor",
      ];
    }
  };

  const handleInputChange = (field, value) => {
    if (field === "ebay_listing.AvailableQuantity" || field === "ebay_listing.SalePrice") {
      console.log("[eBay:PriceQty]", "[Inspector] Field changed", {
        cardId: selectedCard?.id,
        cardStatus: formData.status,
        field,
        value,
      });
    }
    // Field mappings for synchronization between metadata and eBay fields
    const syncFields = {
      // Basic fields
      title: { ebay: "ebay_listing.Title" },
      description: { ebay: "ebay_listing.Description" },
      category: { ebay: "ebay_listing.Category" },
      condition: { ebay: "ebay_listing.Condition" },
      // Detailed metadata fields that sync with eBay ItemSpecifics
      "Card Name": {
        ebay: "ebay_listing.ItemSpecifics.Card Name",
        metadataField: "Card Name",
      },
      Set: {
        ebay: "ebay_listing.ItemSpecifics.Set",
        metadataField: "Set",
      },
      SubSet: {
        ebay: "ebay_listing.ItemSpecifics.SubSet",
        metadataField: "SubSet",
      },
      Variant: {
        ebay: "ebay_listing.ItemSpecifics.Variant",
        metadataField: "Variant",
      },
      Year: {
        ebay: "ebay_listing.ItemSpecifics.Year",
        metadataField: "Year",
      },
      Rarity: {
        ebay: "ebay_listing.ItemSpecifics.Rarity",
        metadataField: "Rarity",
      },
      Condition: {
        ebay: "ebay_listing.ItemSpecifics.Condition",
        metadataField: "Condition",
      },
    };

    if (field.startsWith("detailed_metadata.")) {
      const detailField = field.replace("detailed_metadata.", "");
      const syncConfig = syncFields[detailField];

      setFormData((prev) => {
        const updated = {
          ...prev,
          detailed_metadata: {
            ...prev.detailed_metadata,
            [detailField]: value,
          },
        };

        // Sync to eBay ItemSpecifics if field has a mapping
        if (syncConfig && syncConfig.ebay) {
          const ebayPath = syncConfig.ebay.replace(
            "ebay_listing.ItemSpecifics.",
            ""
          );
          updated.ebay_listing = {
            ...prev.ebay_listing,
            ItemSpecifics: {
              ...prev.ebay_listing.ItemSpecifics,
              [ebayPath]: value,
            },
          };
        }

        // Condition field is handled separately from metadata, so skip it here

        return updated;
      });
    } else if (field.startsWith("ebay_listing.")) {
      const ebayField = field.replace("ebay_listing.", "");

      if (ebayField.startsWith("ItemSpecifics.")) {
        const itemField = ebayField.replace("ItemSpecifics.", "");

        setFormData((prev) => {
          const updated = {
            ...prev,
            ebay_listing: {
              ...prev.ebay_listing,
              ItemSpecifics: {
                ...prev.ebay_listing.ItemSpecifics,
                [itemField]: value,
              },
            },
          };

          // Sync to detailed_metadata if field has a mapping (excluding Condition)
          const syncConfig = syncFields[itemField];
          if (
            syncConfig &&
            syncConfig.metadataField &&
            itemField !== "Condition"
          ) {
            updated.detailed_metadata = {
              ...prev.detailed_metadata,
              [syncConfig.metadataField]: value,
            };
          }

          return updated;
        });
      } else {
        // Handle top-level eBay fields (Title, Description, Category, Condition)
        setFormData((prev) => {
          const updated = {
            ...prev,
            ebay_listing: {
              ...prev.ebay_listing,
              [ebayField]: value,
            },
          };

          // Sync Title to title, Description to description, Category to category
          if (ebayField === "Title") {
            updated.title = value;
          } else if (ebayField === "Description") {
            updated.description = value;
          } else if (ebayField === "Category") {
            updated.category = value;
          } else if (ebayField === "Condition") {
            // Sync Condition to basic condition (but NOT to ItemSpecifics)
            updated.condition = value;
          } else if (ebayField === "Category") {
            // Sync Category to basic category
            updated.category = value;
          }

          return updated;
        });
      }
    } else {
      // Handle basic fields (title, description, category, condition)
      setFormData((prev) => {
        const updated = {
          ...prev,
          [field]: value,
        };

        // Handle category - save to metadata.category and sync to ebay_listing.Category
        if (field === "category") {
          updated.ebay_listing = {
            ...prev.ebay_listing,
            Category: value,
          };
        }

        // Handle condition - save to metadata.condition and sync to ebay_listing.Condition (but NOT ItemSpecifics)
        if (field === "condition") {
          updated.ebay_listing = {
            ...prev.ebay_listing,
            Condition: value,
          };
        }

        // Sync to eBay fields if field has a mapping (for other fields)
        const syncConfig = syncFields[field];
        if (syncConfig && syncConfig.ebay) {
          const ebayPath = syncConfig.ebay.replace("ebay_listing.", "");
          updated.ebay_listing = {
            ...prev.ebay_listing,
            [ebayPath]: value,
          };
        }

        return updated;
      });
    }
  };

  const buildUpdatePayload = (card, data) => {
    const defaultTitle = formatTitleBuilderConfig(card);
    const currentTitle = data.title?.toString().trim() || "";
    const defaultTitleTrimmed = defaultTitle.trim();

    // Prepare update payload - exclude isTitleEdited from formData first
    const { isTitleEdited: _, ...formDataWithoutFlag } = data;
    const updatePayload = {
      ...formDataWithoutFlag,
    };

    // Handle title field logic
    if (currentTitle === "") {
      // Empty title means reset to default
      updatePayload.isTitleEdited = false;
      updatePayload.ebay_listing = {
        ...updatePayload.ebay_listing,
        Title: defaultTitle,
      };
    } else if (currentTitle === defaultTitleTrimmed) {
      // If edited value matches default, reset the flag
      updatePayload.isTitleEdited = false;
      updatePayload.ebay_listing = {
        ...updatePayload.ebay_listing,
        Title: defaultTitle,
      };
    } else {
      // If edited value differs, set flag and save edited value
      updatePayload.isTitleEdited = true;
      updatePayload.ebay_listing = {
        ...updatePayload.ebay_listing,
        Title: currentTitle,
      };
    }

    return updatePayload;
  };

  const handleSave = async ({ silent = false } = {}) => {
    if (!selectedCard || !onSaveCard) {
      return;
    }

    setIsSaving(true);
    try {
      const updatePayload = buildUpdatePayload(selectedCard, formData);
      await onSaveCard(selectedCard.id, updatePayload);
      lastSavedFormDataRef.current = formData;
      if (!silent) {
        toast.success("Card updated successfully");
      }
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Failed to update card");
    } finally {
      setIsSaving(false);
    }
  };

  const handleFieldBlur = async () => {
    // Listed cards allow live quantity/price edits even without full isEditing
    // mode (see isLiveEditableWhenListed in renderField), so autosave applies then too.
    const canAutoSave = isEditing || formData.status === "listed";
    if (!canAutoSave || !selectedCard || isSaving || isAutoSavingRef.current) {
      return;
    }

    const currentFormData = JSON.stringify(formData);
    const lastSavedFormData = JSON.stringify(lastSavedFormDataRef.current || {});

    if (currentFormData === lastSavedFormData) {
      console.log("[eBay:PriceQty]", "[Inspector] Field blurred but nothing changed, skipping save");
      return;
    }

    console.log("[eBay:PriceQty]", "[Inspector] Field blurred with changes - autosaving", {
      cardId: selectedCard?.id,
      isEditing,
      cardStatus: formData.status,
    });
    isAutoSavingRef.current = true;
    try {
      await handleSave({ silent: true });
    } finally {
      isAutoSavingRef.current = false;
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / cardsPerPage));

  const getSelectedCardIndex = () => {
    if (!filteredCards || !selectedCard) return -1;
    return filteredCards.findIndex((c) => c.id === selectedCard.id);
  };

  const isOnFirstCardOfPage = () => getSelectedCardIndex() === 0;
  const isOnLastCardOfPage = () => {
    const idx = getSelectedCardIndex();
    return idx !== -1 && idx >= filteredCards.length - 1;
  };

  const selectCard = (card) => {
    if (!card) return;
    if (typeof onCardSelect === "function") onCardSelect(card);
    if (typeof setSelectedCard === "function") setSelectedCard(card);
  };

  const handleNextCard = () => {
    if (!filteredCards || !selectedCard) return;
    const idx = getSelectedCardIndex();
    if (idx === -1) return;

    if (idx < filteredCards.length - 1) {
      selectCard(filteredCards[idx + 1]);
      return;
    }

    if (nextPage && typeof onFetchPage === "function") {
      onFetchPage(nextPage, "first");
    } else if (filteredCards.length > 0) {
      selectCard(filteredCards[0]);
    }
  };

  const handlePrevCard = () => {
    if (!filteredCards || !selectedCard) return;
    const idx = getSelectedCardIndex();
    if (idx === -1) return;

    if (idx > 0) {
      selectCard(filteredCards[idx - 1]);
      return;
    }

    if (prevPage && typeof onFetchPage === "function") {
      onFetchPage(prevPage, "last");
    }
  };

  const handleInspectorPagePrev = () => {
    if (prevPage && typeof onFetchPage === "function") {
      onFetchPage(prevPage, "first");
    }
  };

  const handleInspectorPageNext = () => {
    if (nextPage && typeof onFetchPage === "function") {
      onFetchPage(nextPage, "first");
    }
  };

  // Local access-token refresh helper (keeps behavior similar to other components)
  const refreshAccessTokenLocal = async () => {
    const refreshToken = localStorage.getItem("refresh");
    if (!refreshToken) {
      localStorage.removeItem("access");
      // Show toast message and logout when no refresh token is found
      toastify("error", "No refresh access found. Please login again.");
      dispatch(signOutRequest({ func: () => {} }));
      throw new Error("No refresh token found. Please login again.");
    }

    const refreshResponse = await fetch(
      `${API_BASE_URL}/api/v1/token/refresh/`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh: refreshToken }),
      }
    );

    if (!refreshResponse.ok) {
      localStorage.removeItem("access");
      localStorage.removeItem("refresh");
      // Show toast message and logout when refresh token fails
      toastify("error", "No refresh access found. Please login again.");
      dispatch(signOutRequest({ func: () => {} }));
      throw new Error("Failed to refresh token. Please login again.");
    }

    const refreshData = await refreshResponse.json();
    localStorage.setItem("access", refreshData.access);
    return refreshData.access;
  };

  const handleStartEditing = async () => {
    if (!selectedCard) return;
    setIsSaving(true);
    try {
      // Update status to draft when starting to edit
      let token = localStorage.getItem("access");
      if (!token) token = await refreshAccessTokenLocal();

      const url = `${API_BASE_URL}/api/v1/cards/${selectedCard.id}/`;
      let response = await fetch(url, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ status: "draft" }),
      });

      if (response.status === 401) {
        const newToken = await refreshAccessTokenLocal();
        response = await fetch(url, {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${newToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ status: "draft" }),
        });
      }

      if (!response.ok) {
        const txt = await response.text().catch(() => "");
        throw new Error(`Failed to update status: ${response.status} ${txt}`);
      }

      const updated = await response.json();
      setFormData((prev) => ({ ...prev, status: "draft" }));
      if (typeof setSelectedCard === "function") {
        setSelectedCard((prev) => ({
          ...(prev || selectedCard),
          status: "draft",
          ...updated,
        }));
      }
      setIsEditing(true);
    } catch (e) {
      console.error("Error updating status to draft:", e);
      toast.error(e?.message || "Failed to update status");
      // Still allow editing even if status update fails
      setIsEditing(true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEditing = () => {
    setIsEditing(false);
  };

  const handleMarkReady = async () => {
    if (!selectedCard) return;
    if (cardHasLongTitle(selectedCard)) {
      toast.error(
        "This card's title exceeds 80 characters. Shorten it before marking as ready to list."
      );
      return;
    }
    setIsSaving(true);

    const navigableBeforeReady = filteredCards;
    const idxBeforeReady = navigableBeforeReady.findIndex(
      (c) => c.id === selectedCard.id
    );
    const wasOnLastCard =
      idxBeforeReady >= 0 &&
      idxBeforeReady === navigableBeforeReady.length - 1;
    const nextCardAfterReady =
      !wasOnLastCard &&
      idxBeforeReady >= 0 &&
      idxBeforeReady < navigableBeforeReady.length - 1
        ? navigableBeforeReady[idxBeforeReady + 1]
        : null;

    // Start animation
    setAnimatingCardId(selectedCard.id);

    try {
      // Always use direct PATCH to only update status field
      let token = localStorage.getItem("access");
      if (!token) token = await refreshAccessTokenLocal();

      const url = `${API_BASE_URL}/api/v1/cards/${selectedCard.id}/`;
      let response = await fetch(url, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ status: "ready" }),
      });

      if (response.status === 401) {
        const newToken = await refreshAccessTokenLocal();
        response = await fetch(url, {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${newToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ status: "ready" }),
        });
      }

      if (!response.ok) {
        const txt = await response.text().catch(() => "");
        throw new Error(`Failed to mark ready: ${response.status} ${txt}`);
      }

      const updated = await response.json();
      setFormData((prev) => ({ ...prev, status: "ready" }));
      if (typeof setSelectedCard === "function") {
        setSelectedCard((prev) => ({
          ...(prev || selectedCard),
          status: "ready",
          ...updated,
        }));
      }

      // Wait for animation to complete, then move to next card / page
      setTimeout(() => {
        setAnimatingCardId(null);
        if (wasOnLastCard) {
          if (nextPage && typeof onFetchPage === "function") {
            onFetchPage(nextPage, "first");
          } else {
            const remaining = draftsOnly
              ? cards.filter(
                  (card) =>
                    (card.status === "draft" || !card.status) &&
                    card.id !== selectedCard.id
                )
              : cards;
            if (remaining.length > 0) {
              selectCard(remaining[0]);
            }
          }
        } else if (nextCardAfterReady) {
          selectCard(nextCardAfterReady);
        } else {
          handleNextCard();
        }
      }, 500); // Animation duration

      if (onRefresh && typeof onRefresh === "function") {
        setTimeout(() => {
          onRefresh();
        }, 2000);
      }

      toast.success("Card status set to Ready");
    } catch (e) {
      console.error("Error marking card ready:", e);
      toast.error(e?.message || "Failed to mark card ready");
      setAnimatingCardId(null);
    } finally {
      setIsSaving(false);
    }
  };

  const openDeleteModal = () => {
    if (!selectedCard) return;
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
  };

  const handleDeleteCard = async () => {
    if (!selectedCard) return;
    setIsDeleting(true);
    try {
      let token = localStorage.getItem("access");
      if (!token) token = await refreshAccessTokenLocal();

      const url = `${API_BASE_URL}/api/v1/cards/bulk-delete/`;
      const requestBody = { ids: [selectedCard.id] };

      let response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      if (response.status === 401) {
        const newToken = await refreshAccessTokenLocal();
        response = await fetch(url, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${newToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        });
      }

      if (!response.ok) {
        throw new Error(`Failed to delete card: ${response.status}`);
      }

      const deletedCardId = selectedCard.id;
      const idx = getSelectedCardIndex();
      const remaining = filteredCards.filter((c) => c.id !== deletedCardId);

      if (remaining.length > 0) {
        const nextIdx = Math.min(idx, remaining.length - 1);
        selectCard(remaining[nextIdx]);
      } else if (typeof setSelectedCard === "function") {
        setSelectedCard(null);
      }

      toast.success("Card deleted successfully");
      setIsDeleteModalOpen(false);

      if (onRefresh && typeof onRefresh === "function") {
        onRefresh();
      }
    } catch (e) {
      console.error("Error deleting card:", e);
      toast.error(e?.message || "Failed to delete card");
    } finally {
      setIsDeleting(false);
    }
  };

  // Helper function to get field value from card - always read from ebay_listing (source of truth)
  const getFieldValueFromCard = (card, fieldName) => {
    const fieldMapping = {
      "Card Name":
        card?.metadata?.ebay_listing?.ItemSpecifics?.["Card Name"] || "",
      Type: card?.metadata?.ebay_listing?.ItemSpecifics?.["Type"] || "",
      Manufacturer:
        card?.metadata?.ebay_listing?.ItemSpecifics?.["Manufacturer"] || "",
      Set: card?.metadata?.ebay_listing?.ItemSpecifics?.["Set"] || "",
      "Card Number":
        card?.metadata?.ebay_listing?.ItemSpecifics?.["Card Number"] || "",
      "Year Manufactured":
        card?.metadata?.ebay_listing?.ItemSpecifics?.["Year Manufactured"] ||
        "",
    };
    return fieldMapping[fieldName] || "";
  };

  // Map table column names to inspector field paths
  const mapTableColumnToInspectorField = (columnName) => {
    const mapping = {
      // Basic fields
      ebay_title: "title",
      ebay_category: "category",
      ebay_condition: "condition",
      ebay_description: "ebay_listing.Description",
      
      // eBay listing top-level fields
      sale_price: "ebay_listing.SalePrice",
      available_quantity: "ebay_listing.AvailableQuantity",
      
      // ItemSpecifics fields
      ebay_card_name: "ebay_listing.ItemSpecifics.Card Name",
      ebay_card_number: "ebay_listing.ItemSpecifics.Card Number",
      ebay_set: "ebay_listing.ItemSpecifics.Set",
      ebay_subset: "ebay_listing.ItemSpecifics.SubSet",
      ebay_variant: "ebay_listing.ItemSpecifics.Variant",
      ebay_year: "ebay_listing.ItemSpecifics.Year",
      ebay_year_manufactured: "ebay_listing.ItemSpecifics.Year Manufactured",
      ebay_rarity: "ebay_listing.ItemSpecifics.Rarity",
      ebay_manufacturer: "ebay_listing.ItemSpecifics.Manufacturer",
      ebay_country: "ebay_listing.ItemSpecifics.Country of Origin",
      ebay_language: "ebay_listing.ItemSpecifics.Language",
      ebay_autographed: "ebay_listing.ItemSpecifics.Autographed",
      ebay_graded: "ebay_listing.ItemSpecifics.Graded",
      ebay_vintage: "ebay_listing.ItemSpecifics.Vintage",
      ebay_condition_description: "ebay_listing.Condition Description",
      ebay_brand: "ebay_listing.ItemSpecifics.Brand",
      ebay_type: "ebay_listing.ItemSpecifics.Type",
    };
    return mapping[columnName] || null;
  };

  // Get all available fields from formData
  const getAllAvailableFields = () => {
    const fields = [];
    
    // Basic fields (always available)
    if (formData.title !== undefined) fields.push({ path: "title", key: "title", label: "Title", type: "basic" });
    if (formData.category !== undefined) fields.push({ path: "category", key: "category", label: "Category", type: "basic" });
    if (formData.condition !== undefined) fields.push({ path: "condition", key: "condition", label: "Condition", type: "basic" });
    
    // eBay listing top-level fields
    if (formData.ebay_listing) {
      Object.keys(formData.ebay_listing).forEach((key) => {
        if (
          key !== "ItemSpecifics" &&
          key !== "Category" &&
          key !== "Condition" &&
          key !== "SuggestedPrice" &&
          key !== "Title" &&
          (typeof formData.ebay_listing[key] !== "object" ||
            formData.ebay_listing[key] === null ||
            Array.isArray(formData.ebay_listing[key]))
        ) {
          fields.push({
            path: `ebay_listing.${key}`,
            key: key,
            label: key,
            type: "ebay_listing",
          });
        }
      });
    }
    
    // ItemSpecifics fields
    if (formData.ebay_listing?.ItemSpecifics) {
      Object.keys(formData.ebay_listing.ItemSpecifics).forEach((key) => {
        if (key !== "Type" && key !== "Condition") {
          fields.push({
            path: `ebay_listing.ItemSpecifics.${key}`,
            key: key,
            label: key,
            type: "ItemSpecifics",
          });
        }
      });
    }
    
    return fields;
  };

  // Organize fields based on personalized order and visibility
  const organizeFields = () => {
    // Return empty if formData is not ready
    if (!formData || Object.keys(formData).length === 0) {
      return { orderedFields: [], extraFields: [] };
    }
    
    const allFields = getAllAvailableFields();
    const orderedFields = [];
    const extraFields = [];
    const processedPaths = new Set();
    
    // Process fields in personalized order
    if (columnOrder && Array.isArray(columnOrder)) {
      columnOrder.forEach((columnName) => {
        // Skip images and status as they're not form fields
        if (columnName === "images" || columnName === "status") {
          return;
        }
        
        // Check visibility
        const isVisible = columnVisibility[columnName] !== false; // Default to true if not specified
        
        if (isVisible) {
          const inspectorPath = mapTableColumnToInspectorField(columnName);
          if (inspectorPath) {
            const field = allFields.find((f) => f.path === inspectorPath);
            if (field && !processedPaths.has(field.path)) {
              orderedFields.push(field);
              processedPaths.add(field.path);
            }
          }
        }
      });
    }
    
    // Add extra fields (not in personalized order)
    allFields.forEach((field) => {
      if (!processedPaths.has(field.path)) {
        extraFields.push(field);
      }
    });
    
    return { orderedFields, extraFields };
  };

  // Format title builder configuration
  const formatTitleBuilderConfig = (card) => {
    if (
      !batchTitleBuilderConfig ||
      !batchTitleBuilderConfig.configuration ||
      batchTitleBuilderConfig.configuration.length === 0
    ) {
      return (
        card?.metadata?.ebay_listing?.Title ||
        card?.metadata?.title ||
        `Card #${card?.id}`
      );
    }

    // New format: [{ type: "field-name" | "string", value: string }, ...]
    let title = "";

    batchTitleBuilderConfig.configuration.forEach((item) => {
      if (item.type === "field-name") {
        // Get field value from card
        const fieldValue = getFieldValueFromCard(card, item.value || "");
        if (fieldValue) {
          title += fieldValue;
        }
      } else if (item.type === "string") {
        // Add string value directly (preserves spaces)
        title += item.value || "";
      }
    });

    // Trim and return, or fallback to default title
    const finalTitle = title.trim();
    return (
      finalTitle ||
      card?.metadata?.ebay_listing?.Title ||
      card?.metadata?.title ||
      `Card #${card?.id}`
    );
  };

  const formatCardTitle = (card) => {
    // Use title builder configuration if available
    return formatTitleBuilderConfig(card);
  };

  const truncateTitle = (title, maxLength = 20) => {
    if (!title || title.length <= maxLength) {
      return title;
    }
    return title.substring(0, maxLength) + "...";
  };

  const formatCardSubtitle = (card) => {
    const year =
      card.metadata?.ebay_listing?.ItemSpecifics?.["Year"] ||
      card.metadata?.ebay_listing?.ItemSpecifics?.["Year Manufactured"] ||
      "N/A";
    const producer =
      card.metadata?.ebay_listing?.ItemSpecifics?.["Manufacturer"] || "N/A";

    return `${year} ${producer}`.trim();
  };

  // Render a single field based on its path
  const renderField = (field) => {
    const { path, key, label, type } = field;
    let value = "";
    let fieldId = "";
    
    // Get value based on path
    if (path === "title") {
      value = formData.title || "";
      fieldId = "title";
    } else if (path === "category") {
      value = formData.category || "";
      fieldId = "category";
    } else if (path === "condition") {
      value = formData.condition || "";
      fieldId = "condition";
    } else if (path.startsWith("ebay_listing.ItemSpecifics.")) {
      const itemKey = path.replace("ebay_listing.ItemSpecifics.", "");
      value = formData.ebay_listing?.ItemSpecifics?.[itemKey] || "";
      fieldId = `ebay_item_${itemKey.toLowerCase().replace(/\s+/g, "_").replace(/\//g, "_")}`;
    } else if (path.startsWith("ebay_listing.")) {
      const ebayKey = path.replace("ebay_listing.", "");
      value = formData.ebay_listing?.[ebayKey] || "";
      fieldId = `ebay_${ebayKey.toLowerCase().replace(/\s+/g, "_").replace(/\//g, "_")}`;
    }

    // Special handling for Title field
    if (path === "title") {
      return (
        <FieldGroup key={path}>
          <FieldLabel htmlFor={fieldId}>
            {label}
            {selectedCard?.isTitleEdited === true && (
              <EditedBadge>Edited</EditedBadge>
            )}
          </FieldLabel>
          <FieldInput
            id={fieldId}
            type="text"
            value={value}
            onChange={(e) => handleInputChange("title", e.target.value)}
            onBlur={handleFieldBlur}
            disabled={!isEditing}
            placeholder="Enter title or leave empty for default"
            style={
              cardHasLongTitle(selectedCard)
                ? { border: "2px solid #ef4444", boxShadow: "none" }
                : undefined
            }
          />
          <FieldHelperText style={{ color: "#6b7280", fontSize: "0.75rem", marginTop: "0.25rem" }}>
            Empty field will replace title builder by default
          </FieldHelperText>
        </FieldGroup>
      );
    }

    // Special handling for Category field
    if (path === "category") {
      return (
        <FieldGroup key={path}>
          <FieldLabel htmlFor={fieldId}>{label}</FieldLabel>
          <FieldSelect
            id={fieldId}
            value={value}
            onChange={(e) => handleInputChange("category", e.target.value)}
            onBlur={handleFieldBlur}
            disabled={!isEditing}
          >
            <option value="">Select Category</option>
            <option value="Sport Trading Card">Sport Trading Card</option>
            <option value="Non-Sport Trading Card">Non-Sport Trading Card</option>
            <option value="CCG Individual Card">CCG Individual Card</option>
            <option value="Collectible Sports Sticker Singles">Collectible Sports Sticker Singles</option>
            <option value="Collectible Non-Sport Sticker Singles">Collectible Non-Sport Sticker Singles</option>
          </FieldSelect>
        </FieldGroup>
      );
    }

    // Special handling for Condition field
    if (path === "condition") {
      return (
        <FieldGroup key={path}>
          <FieldLabel htmlFor={fieldId}>{label}</FieldLabel>
          <FieldSelect
            id={fieldId}
            value={value}
            onChange={(e) => handleInputChange("condition", e.target.value)}
            onBlur={handleFieldBlur}
            disabled={!isEditing}
          >
            <option value="">Select Condition</option>
            {getConditionOptions(formData.category).map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </FieldSelect>
        </FieldGroup>
      );
    }

    // Handle eBay listing top-level fields
    if (type === "ebay_listing") {
      const isCategoryId = key === "CategoryId" || key === "categoryId";
      // Quantity/price stay editable even on an already-listed card - saving
      // pushes the change live to eBay instead of just the local DB.
      const isLiveEditableWhenListed =
        formData.status === "listed" &&
        (key === "AvailableQuantity" || key === "SalePrice");
      const isDisabled =
        isCategoryId || (!isEditing && !isLiveEditableWhenListed);

      return (
        <FieldGroup key={path}>
          <FieldLabel htmlFor={fieldId}>{label}</FieldLabel>
          {key === "Description" ? (
            <FieldTextarea
              id={fieldId}
              value={value}
              onChange={(e) => handleInputChange(path, e.target.value)}
              onBlur={handleFieldBlur}
              disabled={isDisabled}
            />
          ) : key === "AvailableQuantity" ? (
            <FieldInput
              id={fieldId}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={value}
              onChange={(e) => {
                const nextValue = e.target.value;
                if (/^\d*$/.test(nextValue)) {
                  handleInputChange(path, nextValue);
                }
              }}
              onBlur={handleFieldBlur}
              disabled={isDisabled}
            />
          ) : key === "SalePrice" ? (
            <>
              <CurrencyInputWrapper>
                <CurrencyPrefix>€</CurrencyPrefix>
                <CurrencyInput
                  id={fieldId}
                  type="number"
                  value={value}
                  onChange={(e) => handleInputChange(path, e.target.value)}
                  onBlur={handleFieldBlur}
                  disabled={isDisabled}
                  step="0.01"
                  min="0"
                />
              </CurrencyInputWrapper>
              {formData.ebay_listing?.SuggestedPrice && (
                <FieldHelperText>
                  Suggested Price:{" "}
                  {(() => {
                    const suggestedPrice = formData.ebay_listing.SuggestedPrice;
                    if (typeof suggestedPrice === "string" && suggestedPrice.includes("-")) {
                      const [min, max] = suggestedPrice.split("-");
                      const minNum = parseFloat(min.trim());
                      const maxNum = parseFloat(max.trim());
                      if (!isNaN(minNum) && !isNaN(maxNum)) {
                        const formatter = new Intl.NumberFormat("en-US", {
                          style: "currency",
                          currency: "EUR",
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        });
                        return `${formatter.format(minNum)} - ${formatter.format(maxNum)}`;
                      }
                    }
                    if (typeof suggestedPrice === "object" && suggestedPrice !== null) {
                      const formatter = new Intl.NumberFormat("en-US", {
                        style: "currency",
                        currency: "EUR",
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      });
                      const min = parseFloat(suggestedPrice.min || 0);
                      const max = parseFloat(suggestedPrice.max || 0);
                      if (!isNaN(min) && !isNaN(max)) {
                        return `${formatter.format(min)} - ${formatter.format(max)}`;
                      }
                    }
                    const numValue = parseFloat(suggestedPrice);
                    if (!isNaN(numValue)) {
                      return new Intl.NumberFormat("en-US", {
                        style: "currency",
                        currency: "EUR",
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }).format(numValue);
                    }
                    return suggestedPrice;
                  })()}
                </FieldHelperText>
              )}
            </>
          ) : (
            <FieldInput
              id={fieldId}
              type="text"
              value={value}
              onChange={(e) => handleInputChange(path, e.target.value)}
              onBlur={handleFieldBlur}
              disabled={isDisabled}
            />
          )}
        </FieldGroup>
      );
    }

    // Handle ItemSpecifics fields
    if (type === "ItemSpecifics") {
      const isYesNo = isYesNoField(key);

      return (
        <FieldGroup key={path}>
          <FieldLabel htmlFor={fieldId}>{label}</FieldLabel>
          {isYesNo ? (
            <FieldSelect
              id={fieldId}
              value={value}
              onChange={(e) => handleInputChange(path, e.target.value)}
              onBlur={handleFieldBlur}
              disabled={!isEditing}
            >
              <option value="">Select...</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </FieldSelect>
          ) : (
            <FieldInput
              id={fieldId}
              type="text"
              value={value}
              onChange={(e) => handleInputChange(path, e.target.value)}
              onBlur={handleFieldBlur}
              disabled={!isEditing}
            />
          )}
        </FieldGroup>
      );
    }

    return null;
  };

  return (
    <InspectorWrapper>
    <InspectorContainer>
      <CardListColumn>
        <CardListHeader>
          <span>Cards ({filteredCards.length})</span>
          <ToggleSwitchContainer onClick={() => setDraftsOnly(!draftsOnly)}>
            <ToggleSwitchLabel>drafts only</ToggleSwitchLabel>
            <ToggleSwitch active={draftsOnly}>
              <ToggleSwitchSlider active={draftsOnly} />
            </ToggleSwitch>
          </ToggleSwitchContainer>
        </CardListHeader>
        {filteredCards.map((card) => (
          <CardListItem
            key={card.id}
            ref={(el) => {
              if (el) {
                cardItemRefs.current[card.id] = el;
              } else {
                delete cardItemRefs.current[card.id];
              }
            }}
            selected={selectedCard?.id === card.id}
            status={card.status}
            $isAnimating={animatingCardId === card.id}
            $hasLongTitle={cardHasLongTitle(card)}
            onClick={() => onCardSelect(card)}
          >
            <RadioButton
              type="radio"
              name="card-selection"
              checked={selectedCard?.id === card.id}
              onChange={() => onCardSelect(card)}
            />
            <CardImages>
              <CardThumbnail
                src={card.front_image}
                alt={`${card.id} front`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (onImageClick) {
                    onImageClick(card, card.front_image);
                  }
                }}
                style={{ cursor: "pointer" }}
              />
              <CardThumbnail
                src={card.back_image}
                alt={`${card.id} back`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (onImageClick) {
                    onImageClick(card, card.back_image);
                  }
                }}
                style={{ cursor: "pointer" }}
              />
            </CardImages>
            <StatusBadge status={card.status || "draft"}>
              {card.status || "draft"}
            </StatusBadge>
            <CardInfo>
              <CardTitle 
                title={formatCardTitle(card)}
              >
                {truncateTitle(formatCardTitle(card))}
              </CardTitle>
              <CardSubtitle>{formatCardSubtitle(card)}</CardSubtitle>
            </CardInfo>
          </CardListItem>
        ))}
      </CardListColumn>

      <MiddleColumn>
        <ImageHeader>Image</ImageHeader>
        {selectedCard ? (
          <ImagePreviewContainer>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "1rem",
                alignItems: "center",
              }}
            >
              <ImageLabel>Front image</ImageLabel>
              <ImagePreview
                src={selectedCard.front_image}
                alt={`${selectedCard.id} front`}
                onClick={() => {
                  if (onImageClick) {
                    onImageClick(selectedCard, selectedCard.front_image);
                  } else if (setSelectedImage) {
                    setSelectedImage(selectedCard.front_image);
                  }
                }}
              />
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "1rem",
                alignItems: "center",
              }}
            >
              <ImageLabel>Back image</ImageLabel>
              <ImagePreview
                src={selectedCard.back_image}
                alt={`${selectedCard.id} back`}
                onClick={() => {
                  if (onImageClick) {
                    onImageClick(selectedCard, selectedCard.back_image);
                  } else if (setSelectedImage) {
                    setSelectedImage(selectedCard.back_image);
                  }
                }}
              />
            </div>
          </ImagePreviewContainer>
        ) : (
          <NoSelectionMessage>
            Select a card from the list to view
            <br />
            front and back images
          </NoSelectionMessage>
        )}
      </MiddleColumn>

      <RightColumn>
        <InfoHeader>
          <span>Info</span>
          {selectedCard && (
            <HeaderActions>
              {!isEditing && formData.status !== "listed" ? (
                <ReadyButton
                  $tone="blue"
                  onClick={handleStartEditing}
                  disabled={isSaving}
                >
                  Edit
                </ReadyButton>
              ) : isEditing ? (
                <ReadyButton
                  $tone="grey"
                  onClick={handleCancelEditing}
                  disabled={isSaving}
                >
                  Cancel Editing
                </ReadyButton>
              ) : null}
              <ReadyButton
                $tone="amber"
                onClick={handleMarkReady}
                disabled={
                  isSaving ||
                  formData.status === "ready" ||
                  formData.status === "listed" ||
                  cardHasLongTitle(selectedCard)
                }
                title={
                  cardHasLongTitle(selectedCard)
                    ? "Title exceeds 80 characters — shorten before marking ready"
                    : formData.status === "ready"
                    ? "Already ready"
                    : formData.status === "listed"
                    ? "Already listed"
                    : "Mark as ready to list"
                }
              >
                {isSaving
                  ? "Saving..."
                  : cardHasLongTitle(selectedCard)
                  ? "Title too long"
                  : formData.status === "ready"
                  ? "Already ready"
                  : formData.status === "listed"
                  ? "Already listed"
                  : "Ready to list"}
              </ReadyButton>
            </HeaderActions>
          )}
        </InfoHeader>
        {selectedCard ? (
          <>
            <FormContainer>
              {(() => {
                const { orderedFields, extraFields } = organizeFields();
                return (
                <>
                    {/* Render fields in personalized order */}
                    {orderedFields.map((field) => renderField(field))}
                    
                    {/* Render extra fields (not in personalized order) */}
                    {extraFields.length > 0 && (
                      <>
                        {extraFields.map((field) => renderField(field))}
                      </>
                    )}
                  </>
                );
                                })()}
            </FormContainer>
            <SaveBar>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                  gap: "0.75rem",
                }}
              >
                <DeleteButton onClick={openDeleteModal} disabled={isSaving || isDeleting}>
                  Delete
                </DeleteButton>

                <div style={{ display: "flex", alignItems: "center" }}>
                <PrevButton
                  onClick={handlePrevCard}
                  disabled={
                    isSaving ||
                    !selectedCard ||
                    (isOnFirstCardOfPage() && !prevPage)
                  }
                  title={
                    !selectedCard
                      ? "No card selected"
                      : isOnFirstCardOfPage() && !prevPage
                      ? "First card"
                      : isOnFirstCardOfPage() && prevPage
                      ? "Go to previous page"
                      : "Go to previous card"
                  }
                >
                  Prev
                </PrevButton>
                <NextButton
                  onClick={handleNextCard}
                  disabled={
                    isSaving ||
                    !selectedCard ||
                    (isOnLastCardOfPage() && !nextPage)
                  }
                  title={
                    !selectedCard
                      ? "No card selected"
                      : isOnLastCardOfPage() && !nextPage
                      ? "Last card"
                      : isOnLastCardOfPage() && nextPage
                      ? "Go to next page"
                      : "Go to next card"
                  }
                >
                  Next
                </NextButton>
                </div>

                <ReadyButton
                  $tone="amber"
                  onClick={handleMarkReady}
                  disabled={
                    isSaving ||
                    formData.status === "ready" ||
                    formData.status === "listed" ||
                    cardHasLongTitle(selectedCard)
                  }
                  title={
                    cardHasLongTitle(selectedCard)
                      ? "Title exceeds 80 characters — shorten before marking ready"
                      : formData.status === "ready"
                      ? "Already ready"
                      : formData.status === "listed"
                      ? "Already listed"
                      : "Mark as ready to list"
                  }
                >
                  {isSaving
                    ? "Saving..."
                    : cardHasLongTitle(selectedCard)
                    ? "Title too long"
                    : formData.status === "ready"
                    ? "Already ready"
                    : formData.status === "listed"
                    ? "Already listed"
                    : "Ready to list"}
                </ReadyButton>
              </div>
            </SaveBar>
          </>
        ) : (
          <NoCardMessage>
            Select a card from the list to view
            <br />
            and edit its details
          </NoCardMessage>
        )}
      </RightColumn>
    </InspectorContainer>

    <InspectorPagination>
      <InspectorPaginationButton
        onClick={handleInspectorPagePrev}
        disabled={isSaving || !prevPage}
        title={!prevPage ? "First page" : "Previous page"}
      >
        Previous
      </InspectorPaginationButton>
      <InspectorPageNumber>
        Page {currentPage} of {totalPages}
      </InspectorPageNumber>
      <InspectorPaginationButton
        onClick={handleInspectorPageNext}
        disabled={isSaving || !nextPage}
        title={!nextPage ? "Last page" : "Next page"}
      >
        Next
      </InspectorPaginationButton>
    </InspectorPagination>

    {isDeleteModalOpen && (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000,
          padding: "1rem",
        }}
      >
        <div
          style={{
            background: "white",
            borderRadius: "12px",
            boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
            width: "100%",
            maxWidth: "400px",
            padding: "1.5rem",
          }}
        >
          <h3
            style={{
              margin: "0 0 1rem 0",
              fontSize: "1.25rem",
              fontWeight: 700,
              color: "#111827",
            }}
          >
            Delete Card
          </h3>
          <p
            style={{
              margin: "0 0 1.5rem 0",
              color: "#6b7280",
            }}
          >
            Are you sure you want to delete this card? This action cannot be
            undone.
          </p>
          <div
            style={{
              display: "flex",
              gap: "0.75rem",
              justifyContent: "flex-end",
            }}
          >
            <button
              onClick={closeDeleteModal}
              disabled={isDeleting}
              style={{
                padding: "0.75rem 1.5rem",
                border: "none",
                borderRadius: "8px",
                fontSize: "0.875rem",
                fontWeight: 600,
                cursor: isDeleting ? "not-allowed" : "pointer",
                transition: "all 0.2s ease",
                background: "#6b7280",
                color: "white",
                opacity: isDeleting ? 0.6 : 1,
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteCard}
              disabled={isDeleting}
              style={{
                padding: "0.75rem 1.5rem",
                border: "none",
                borderRadius: "8px",
                fontSize: "0.875rem",
                fontWeight: 600,
                cursor: isDeleting ? "not-allowed" : "pointer",
                transition: "all 0.2s ease",
                background: "#ef4444",
                color: "white",
                opacity: isDeleting ? 0.6 : 1,
              }}
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </button>
          </div>
        </div>
      </div>
    )}
    </InspectorWrapper>
  );
};

export default CardInspector;
